/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme';
import { adService } from '../services/adService';
import { 
  Megaphone, 
  PlusCircle, 
  Trash2, 
  Edit, 
  ExternalLink, 
  Calendar,
  Layout,
  Clock,
  ArrowRight,
  AlertCircle,
  BarChart3,
  X
} from 'lucide-react';
import { ROUTES } from '../routes';

interface Ad {
  id: number;
  title: string;
  content: string;
  placement: string;
  status: string;
  startDate: string;
  endDate: string;
  imageUrl?: string;
  targetUrl?: string;
}

export default function AdManagement() {
  const theme = useTheme();
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newAd, setNewAd] = useState({
    title: '',
    content: '',
    placement: 'sidebar',
    imageUrl: '',
    targetUrl: ''
  });
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.sellerPlan !== 'Pro') {
      navigate('/plans');
      return;
    }
    fetchAds();
  }, [navigate]);

  const fetchAds = async () => {
    try {
      const response = await adService.getMyAds();
      setAds(response.data.data || []);
    } catch (err) {
      setError('Failed to load advertisements.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAd = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adService.createAd(newAd);
      setShowCreate(false);
      setNewAd({ title: '', content: '', placement: 'sidebar', imageUrl: '', targetUrl: '' });
      fetchAds();
    } catch (err) {
      alert('Failed to create advertisement.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.colors.primary }}></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ color: theme.colors.text }}>Ad Management</h1>
          <p style={{ color: theme.colors.mutedText }}>Boost your listings and reach more local buyers.</p>
        </div>
        <button 
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center px-6 py-3 rounded-xl font-black text-white transition-all hover:scale-[1.02] shadow-lg shadow-blue-500/20"
          style={{ backgroundColor: theme.colors.primary }}
        >
          <PlusCircle size={20} className="mr-2" />
          Create New Ad
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl border font-bold flex items-center gap-2" 
             style={{ backgroundColor: theme.colors.danger + '10', borderColor: theme.colors.danger, color: theme.colors.danger }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-6 rounded-3xl border shadow-sm flex items-center gap-4" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
          <div className="p-3 rounded-2xl bg-blue-50 text-blue-500">
            <Megaphone size={24} />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-wider opacity-40">Active Ads</p>
            <p className="text-2xl font-black">{ads.filter(a => a.status === 'active').length}</p>
          </div>
        </div>
        <div className="p-6 rounded-3xl border shadow-sm flex items-center gap-4" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
          <div className="p-3 rounded-2xl bg-emerald-50 text-emerald-500">
            <BarChart3 size={24} />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-wider opacity-40">Total Reach</p>
            <p className="text-2xl font-black">1.2k</p>
          </div>
        </div>
        <div className="p-6 rounded-3xl border shadow-sm flex items-center gap-4" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
          <div className="p-3 rounded-2xl bg-amber-50 text-amber-500">
            <Clock size={24} />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-wider opacity-40">Pending Approval</p>
            <p className="text-2xl font-black">{ads.filter(a => a.status !== 'active').length}</p>
          </div>
        </div>
      </div>

      {/* Ads Table / List */}
      <div className="rounded-[2.5rem] border shadow-sm overflow-hidden" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b" style={{ borderColor: theme.colors.border }}>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest opacity-40">Campaign Info</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest opacity-40">Placement</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest opacity-40">Status</th>
                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest opacity-40 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: theme.colors.border }}>
              {ads.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-8 py-20 text-center">
                    <div className="max-w-xs mx-auto space-y-4">
                      <Megaphone size={48} className="mx-auto opacity-10" />
                      <p className="font-bold text-lg opacity-40">You haven't created any advertisements yet.</p>
                      <button onClick={() => setShowCreate(true)} className="text-sm font-black" style={{ color: theme.colors.primary }}>
                        Launch your first campaign <ArrowRight size={14} className="inline ml-1" />
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                ads.map((ad) => (
                  <tr key={ad.id} className="group hover:bg-gray-50 transition-colors">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Layout size={20} className="opacity-30" />
                        </div>
                        <div>
                          <div className="font-black text-sm" style={{ color: theme.colors.text }}>{ad.title}</div>
                          <div className="text-xs flex items-center gap-3 mt-1" style={{ color: theme.colors.mutedText }}>
                            <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(ad.startDate).toLocaleDateString()}</span>
                            {ad.targetUrl && <span className="flex items-center gap-1"><ExternalLink size={12} /> {new URL(ad.targetUrl).hostname}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-lg bg-gray-100" style={{ color: theme.colors.mutedText }}>
                        {ad.placement}
                      </span>
                    </td>
                    <td className="px-8 py-6">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        ad.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${ad.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                        {ad.status}
                      </span>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button className="p-2.5 rounded-xl border hover:bg-white hover:shadow-sm transition-all" style={{ borderColor: theme.colors.border }}>
                          <Edit size={16} style={{ color: theme.colors.mutedText }} />
                        </button>
                        <button className="p-2.5 rounded-xl border border-red-100 hover:bg-red-50 transition-all text-red-500">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)}></div>
          <div className="relative w-full max-w-2xl rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in duration-300" 
               style={{ backgroundColor: theme.colors.surface }}>
            <div className="px-10 py-8 border-b flex justify-between items-center" style={{ borderColor: theme.colors.border }}>
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl text-white shadow-lg" style={{ backgroundColor: theme.colors.primary }}>
                  <Megaphone size={20} />
                </div>
                <h3 className="text-2xl font-black tracking-tight" style={{ color: theme.colors.text }}>Launch Ad Campaign</h3>
              </div>
              <button onClick={() => setShowCreate(false)} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleCreateAd} className="p-10 space-y-6">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>Campaign Title</label>
                <input
                  type="text"
                  required
                  value={newAd.title}
                  placeholder="Summer Sale Boost"
                  onChange={(e) => setNewAd({ ...newAd, title: e.target.value })}
                  className="w-full px-5 py-3.5 rounded-2xl border focus:outline-none focus:ring-4 transition-all"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>Ad Copy / Content</label>
                <textarea
                  required
                  value={newAd.content}
                  placeholder="Get 20% off all vintage items this weekend only!"
                  onChange={(e) => setNewAd({ ...newAd, content: e.target.value })}
                  className="w-full px-5 py-3.5 rounded-2xl border focus:outline-none focus:ring-4 transition-all h-28 resize-none"
                  style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>Ad Placement</label>
                  <select
                    value={newAd.placement}
                    onChange={(e) => setNewAd({ ...newAd, placement: e.target.value })}
                    className="w-full px-5 py-3.5 rounded-2xl border focus:outline-none focus:ring-4 transition-all appearance-none"
                    style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                  >
                    <option value="sidebar">Sidebar (Category Pages)</option>
                    <option value="banner">Top Banner (Search Results)</option>
                    <option value="feed">In-Feed (Home Page)</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-black uppercase tracking-wider ml-1" style={{ color: theme.colors.mutedText }}>Destination URL</label>
                  <input
                    type="url"
                    placeholder="https://yourstore.com/listing/123"
                    value={newAd.targetUrl}
                    onChange={(e) => setNewAd({ ...newAd, targetUrl: e.target.value })}
                    className="w-full px-5 py-3.5 rounded-2xl border focus:outline-none focus:ring-4 transition-all"
                    style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                  />
                </div>
              </div>

              <div className="pt-4 flex gap-4">
                <button 
                  type="button" 
                  onClick={() => setShowCreate(false)}
                  className="flex-1 py-4 px-6 rounded-2xl border font-bold transition-all hover:bg-gray-50"
                  style={{ borderColor: theme.colors.border, color: theme.colors.text }}
                >
                  Save as Draft
                </button>
                <button 
                  type="submit"
                  className="flex-1 py-4 px-6 rounded-2xl font-black text-white transition-all hover:opacity-90 shadow-xl"
                  style={{ backgroundColor: theme.colors.primary }}
                >
                  Submit for Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
